import { useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { ScheduleRequestEventData } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../../../constants';
import { EventCardComponent } from '../../../schedule-calendar-components/event-card';
import { OverlappingEventIndicatorVariantType } from '../../calendar-view-v3/overlapping-event-indicator';
import { getDurationFromHours } from '../../utils';
import { GridEventCardBase } from '../atoms/grid-event-card-base';

type GridScheduleRequestCardProps = {
  id: string;
  title: string;
  startDate: string;
  startHour: string;
  endHour: string;
  locationName?: string;
  locationId?: string;
  entityId?: string;
  entityName?: string;
  personStatus?: string;
  eventTypeColor?: string;
  backgroundColor?: string;
  overlappingIndicatorVariant?: OverlappingEventIndicatorVariantType;
  isYesterdayEvent?: boolean;
  scheduleRequestEventData?: ScheduleRequestEventData;
};

//#region Card

export const GridScheduleRequestCard = ({
  id,
  title,
  startDate,
  startHour,
  endHour,
  locationName = '',
  locationId,
  entityId,
  entityName,
  personStatus,
  eventTypeColor,
  backgroundColor = theme.colors.white,
  overlappingIndicatorVariant,
  isYesterdayEvent,
  scheduleRequestEventData,
}: GridScheduleRequestCardProps) => {
  const duration = useMemo(() => getDurationFromHours(startHour, endHour), [startHour, endHour]);
  const primaryColor = eventTypeColor ?? PRIMARY_EVENT_COLOR;

  return (
    <GridEventCardBase primaryColor={primaryColor} backgroundColor={backgroundColor} isRequest isSelected={false}>
      <GridEventCardBase.ArticleShell>
        <GridEventCardBase.PersonStatusBar personStatus={personStatus} />
        <GridEventCardBase.EventColoredLine />
        <EventCardComponent
          id={id}
          eventType='request'
          title={title}
          duration={duration}
          locationName={locationName}
          locationId={locationId}
          providerId={entityId}
          providerName={entityName}
          isExpanded={false}
          startDate={startDate}
          overlappingIndicatorVariant={overlappingIndicatorVariant}
          isYesterdayEvent={isYesterdayEvent}
          scheduleRequestEventData={scheduleRequestEventData}
        />
      </GridEventCardBase.ArticleShell>
    </GridEventCardBase>
  );
};

//#endregion

//#region Tooltip

type GridScheduleRequestTooltipProps = {
  startHour: string;
  endHour: string;
  scheduleRequestEventData?: ScheduleRequestEventData;
  appointmentType?: string;
};

export const GridScheduleRequestTooltip = ({
  startHour,
  endHour,
  scheduleRequestEventData,
  appointmentType,
}: GridScheduleRequestTooltipProps) => {
  const { t } = useTranslation('schedule');
  const duration = useMemo(() => getDurationFromHours(startHour, endHour), [startHour, endHour]);

  const allRequestedOpenings = scheduleRequestEventData?.allRequestedOpenings;
  const totalRequestedOpenings = allRequestedOpenings?.length ?? 0;
  const isMultipleOpenings = totalRequestedOpenings > 1;

  const name = isMultipleOpenings ? t('All Requested Times') : t('Requested Appointment');
  const personName = getFullName(scheduleRequestEventData?.schedulee ?? {});
  const resolvedAppointmentType = scheduleRequestEventData?.appointmentTypeDetails?.displayName || appointmentType;

  return (
    <div css={scheduleRequestTooltipStyle}>
      <Text weight='bold' size='medium' color='white'>
        {name}
      </Text>
      {!!personName && (
        <Text size='medium' color='white'>
          {personName}
        </Text>
      )}
      {!!resolvedAppointmentType && (
        <Text size='medium' color='white'>
          {resolvedAppointmentType}
        </Text>
      )}
      {isMultipleOpenings ? (
        allRequestedOpenings?.map((opening, index) => {
          const openingDuration = getDurationFromHours(opening.startTime, opening.endTime);
          return (
            <Text key={index} size='small' color='white'>
              {openingDuration}
            </Text>
          );
        })
      ) : (
        <Text size='small' color='white'>
          {duration}
        </Text>
      )}
    </div>
  );
};

const scheduleRequestTooltipStyle = {
  display: 'grid',
  gap: theme.spacing(0.25),
};

//#endregion
